import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { FadeUp, Eyebrow } from "./Reveal";
import { PHONE_DISPLAY, PHONE_TEL, WHATSAPP_URL } from "../constants";
import { WhatsAppIcon } from "./Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "h-12 rounded-xl bg-white/[0.04] border-white/10 text-[#F1F5F9] placeholder:text-[#94A3B8]/60 focus-visible:ring-[#FF6B00]";

export const ContactForm = () => {
  const [form, setForm] = useState({ nome: "", telefono: "", tipo: "", email: "", via: "", messaggio: "" });
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const offerta = searchParams.get("offerta");

  useEffect(() => {
    if (offerta) {
      setForm((f) => ({
        ...f,
        tipo: "caldaia",
        messaggio: `Richiedo l'offerta: ${offerta}`,
      }));
    }
  }, [offerta]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nome.trim() || !form.telefono.trim() || !form.tipo) {
      toast.error("Compila nome, telefono e tipo di richiesta.");
      return;
    }
    setLoading(true);
    try {
      const payload = { ...form };
      if (!payload.email.trim()) delete payload.email;
      if (!payload.via.trim()) delete payload.via;
      await axios.post(`${API}/leads`, payload);
      toast.success("Richiesta inviata! Ti ricontattiamo al più presto.");
      setForm({ nome: "", telefono: "", tipo: "", email: "", via: "", messaggio: "" });
    } catch {
      toast.error("Errore nell'invio. Riprova o chiamaci direttamente.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contatti" data-testid="contact-section" className="py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-16">
        <FadeUp>
          <Eyebrow>Preventivo gratuito</Eyebrow>
          <h1 className="font-display font-semibold tracking-tighter text-4xl sm:text-5xl lg:text-6xl mt-4">
            Raccontaci il problema.
            <span className="text-[#94A3B8]"> Al resto pensiamo noi.</span>
          </h1>
          <p className="mt-6 max-w-md text-base md:text-lg text-[#94A3B8]">
            Compila il modulo e ti richiamiamo per un preventivo gratuito e senza
            impegno. Per le urgenze, chiamaci direttamente.
          </p>
          <div className="mt-10 flex flex-col gap-4">
            <a
              href={PHONE_TEL}
              data-testid="contact-phone-link"
              className="font-display font-medium text-2xl md:text-3xl tracking-tight text-[#FF6B00] hover:underline underline-offset-8 w-fit"
            >
              {PHONE_DISPLAY}
            </a>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp-link"
              className="inline-flex items-center gap-3 text-[#94A3B8] hover:text-[#25D366] transition-colors duration-300 w-fit"
            >
              <WhatsAppIcon className="w-5 h-5" />
              <span className="text-sm uppercase tracking-[0.2em] font-mono-tech">
                Scrivici su WhatsApp
              </span>
            </a>
          </div>
        </FadeUp>

        <FadeUp delay={0.15}>
          <form
            onSubmit={submit}
            data-testid="preventivo-form"
            className="glass rounded-3xl p-8 md:p-10 flex flex-col gap-5"
          >
            {offerta && (
              <div
                data-testid="offerta-selezionata-badge"
                className="rounded-2xl border border-[#FF6B00]/40 bg-[#FF6B00]/10 px-5 py-4"
              >
                <p className="font-mono-tech text-[11px] uppercase tracking-[0.2em] text-[#FF6B00] mb-1">
                  Offerta selezionata
                </p>
                <p className="text-sm text-[#F1F5F9]">{offerta}</p>
                <p className="text-xs text-[#94A3B8] mt-2">
                  Compila solo i tuoi dati: la richiesta ci arriva già con l'offerta scelta.
                </p>
              </div>
            )}
            <div>
              <label htmlFor="nome" className="font-mono-tech text-xs uppercase tracking-[0.2em] text-[#94A3B8] block mb-2">
                Nome e cognome
              </label>
              <Input
                id="nome"
                data-testid="form-nome-input"
                className={inputCls}
                placeholder="Mario Rossi"
                value={form.nome}
                onChange={(e) => setForm({ ...form, nome: e.target.value })}
              />
            </div>
            <div>
              <label htmlFor="telefono" className="font-mono-tech text-xs uppercase tracking-[0.2em] text-[#94A3B8] block mb-2">
                Telefono
              </label>
              <Input
                id="telefono"
                type="tel"
                data-testid="form-telefono-input"
                className={inputCls}
                placeholder="333 123 4567"
                value={form.telefono}
                onChange={(e) => setForm({ ...form, telefono: e.target.value })}
              />
            </div>
            <div>
              <label htmlFor="email" className="font-mono-tech text-xs uppercase tracking-[0.2em] text-[#94A3B8] block mb-2">
                Email (facoltativa — per la conferma automatica)
              </label>
              <Input
                id="email"
                type="email"
                data-testid="form-email-input"
                className={inputCls}
                placeholder="mario.rossi@email.it"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </div>
            <div>
              <label htmlFor="via" className="font-mono-tech text-xs uppercase tracking-[0.2em] text-[#94A3B8] block mb-2">
                Via / indirizzo (facoltativo)
              </label>
              <Input
                id="via"
                data-testid="form-via-input"
                className={inputCls}
                placeholder="Es. Via Appia Nuova 120, Roma"
                value={form.via}
                onChange={(e) => setForm({ ...form, via: e.target.value })}
              />
            </div>
            {!offerta && (
            <div>
              <label className="font-mono-tech text-xs uppercase tracking-[0.2em] text-[#94A3B8] block mb-2">
                Tipo di richiesta
              </label>
              <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                <SelectTrigger data-testid="form-tipo-select" className={inputCls}>
                  <SelectValue placeholder="Seleziona..." />
                </SelectTrigger>
                <SelectContent className="bg-[#12151C] border-white/10 text-[#F1F5F9]">
                  <SelectItem value="caldaia" data-testid="form-tipo-caldaia">Caldaia</SelectItem>
                  <SelectItem value="condizionatore" data-testid="form-tipo-condizionatore">Condizionatore</SelectItem>
                  <SelectItem value="altro" data-testid="form-tipo-altro">Altro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            )}
            <div>
              <label htmlFor="messaggio" className="font-mono-tech text-xs uppercase tracking-[0.2em] text-[#94A3B8] block mb-2">
                Messaggio (facoltativo)
              </label>
              <Textarea
                id="messaggio"
                data-testid="form-messaggio-input"
                className={`${inputCls} min-h-[110px] py-3`}
                placeholder="Es. la caldaia va in blocco e non parte l'acqua calda..."
                value={form.messaggio}
                onChange={(e) => setForm({ ...form, messaggio: e.target.value })}
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="submit-preventivo-button"
              className="mt-2 flex items-center justify-center gap-2 rounded-full bg-[#FF6B00] text-[#07090D] font-semibold px-8 py-4 text-sm uppercase tracking-wider transition-all duration-300 hover:shadow-[0_0_40px_rgba(255,107,0,0.45)] active:scale-[0.98] disabled:opacity-60"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {loading ? "Invio in corso..." : "Invia richiesta"}
            </button>
          </form>
        </FadeUp>
      </div>
    </section>
  );
};
